import React, { useState, useEffect, useMemo, useRef } from "react";
import ClientChart from "../ClientChart/ClientChart";
import ClientDetails from "../ClientDetails/ClientDetails";
import AddClientModal from "../../../components/styled-components/modal/addClientModal/AddClientModal";
import addIcon from "../../../assets/SVGs/add-white-icon.svg";
import searchIcon from "../../../assets/SVGs/magnify-glass-icon-grey.svg";
import DASHBOARD_API from "../../../apis/dashboard.api";
import DEATH_API from "../../../apis/death.api";
import { MODULE_API } from "../../../apis";
import FINANCIAL_ADVISOR_API from "../../../apis/financialAdvisor.api";
import { useSelector, useDispatch } from "react-redux";
import { setDelegateUsers } from "../../../redux/slices/financialAdvisorSlice";
import ModalVideo from "../../ModuleVideos/ModalVideo";
import DeactivateClientModal from "../DeactivateClientModal/DeactivateClientModal";
import swal from "sweetalert";
import { debounce } from "lodash";
import { Button, Icon, Spin, Pagination, Modal } from "antd";
import "./ProfessionalViewDashboard.css";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

const ProfessionalViewDashboard = () => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [reset, setReset] = useState(false);

  const [query, setQuery] = useState("");
  const [prospectPassUsedData, setProspectPassUsedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showloading, setShowLoading] = useState(false);
  const [clientsData, setClientsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [showFallback, setFallback] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null); // null, 'active', 'inactive', 'prospect'
  const [advisorClientBankAccounts, setAdvisorClientBankAccounts] = useState(
    []
  );
  const [stats, setStats] = useState({
    totalSlots: 10,
    activeClients: 0,
    availableSeats: 10,
    totalClients: 0,
    prospectPassAvailable: 0,
  });
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    email: "",
    phone: "",
    zipCode: "",
  });

  // Deactivation Modal State
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  const [selectedClientForDeactivation, setSelectedClientForDeactivation] = useState(null);
  const [notifyClient, setNotifyClient] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [processingClientId, setProcessingClientId] = useState(null);
  const [refreshingClientId, setRefreshingClientId] = useState(null);
  const [prospectPassStatus, setProspectPassStatus] = useState(null);
  const [prospectPassModalVisible, setProspectPassModalVisible] =
    useState(false);
  const [bankAccountsModalVisible, setBankAccountsModalVisible] =
    useState(false);
  const [selectedBankClient, setSelectedBankClient] = useState(null);

  const isShowClientBtn = useSelector(
    (state) => state.rootReducer.financialAdvisor.showAddClientButton
  );
  const plaidProcessingData = useSelector(
    (state) => state.rootReducer.financialAdvisor.plaidProcessingData
  );
  const dispatch = useDispatch();

  const scrollRef = useRef();
  const latestQueryRef = useRef("");
  const requestIdRef = useRef(0);

  const debouncedSearch = useMemo(() => {
    return debounce((searchText) => {
      latestQueryRef.current = searchText; // track latest valid search
      getClientListingData(1, searchText, true);
    }, 500);
  }, []);

  const clientBankAccountsMap = useMemo(() => {
    return advisorClientBankAccounts.reduce((acc, entry) => {
      if (entry && entry.clientId != null) {
        acc[String(entry.clientId)] = Array.isArray(entry.accounts)
          ? entry.accounts
          : [];
      }
      return acc;
    }, {});
  }, [advisorClientBankAccounts]);

  const getClientBankSummary = (clientId) => {
    const accounts = clientBankAccountsMap[String(clientId)] || [];
    const uniqueBanks = new Set(
      accounts.map((account) => account && account.bankName).filter(Boolean)
    );

    const unauthorizedAccounts = accounts.filter((account) => {
      const status = String(
        account && account.accountStatus ? account.accountStatus : ""
      )
        .trim()
        .toLowerCase();
      return status === "unauthorized";
    });

    const authorizedAccounts = accounts.filter((account) => {
      const status = String(
        account && account.accountStatus ? account.accountStatus : ""
      )
        .trim()
        .toLowerCase();
      return status === "authorized";
    });

    return {
      accounts,
      totalAccounts: accounts.length,
      totalBanks: uniqueBanks.size,
      authorizedAccounts: authorizedAccounts.length,
      unauthorizedAccounts: unauthorizedAccounts.length,
      hasData: accounts.length > 0,
    };
  };

  const openBankAccountsModal = (clientData, bankSummary) => {
    setSelectedBankClient({
      clientId: clientData && clientData.client ? clientData.client.id : null,
      clientName:
        clientData && clientData.client ? clientData.client.name : "Client",
      ...bankSummary,
    });
    setBankAccountsModalVisible(true);
  };

  const closeBankAccountsModal = () => {
    setBankAccountsModalVisible(false);
    setSelectedBankClient(null);
  };

  useEffect(() => {
    const normalizedQuery = query.trim().replace(/\s+/g, " ");
    latestQueryRef.current = query;

    if (normalizedQuery.trim() === "") {
      // If query is empty, cancel any pending debounced call
      debouncedSearch.cancel();
      setClientsData([]);
      setFallback(false);
      getClientListingData(1, "", false); // fetch    default list
    } else {
      setClientsData([]);
      setFallback(false);
      debouncedSearch(normalizedQuery); // debounced search
    }
  }, [query]);

  const handleChange = (e) => {
    const rawInput = e.target.value;

    const sanitized = rawInput.replace(/[^a-zA-Z0-9\s]/g, "");

    // Step 2: Replace multiple spaces with one (but NOT trimming yet, to allow natural typing)
    const singleSpaced = sanitized.replace(/\s{2,}/g, " ");

    setQuery(singleSpaced);
  };

  const getClientListingData = async (
    page = 1,
    searchQuery = "",
    isFreshSearch = false,
    limit = 10,
    filter = activeFilter
  ) => {
    const currentRequestId =
      page === 1 || isFreshSearch
        ? ++requestIdRef.current
        : requestIdRef.current;
    try {
      if (page > 1 && !isFreshSearch) {
        setIsFetchingNextPage(true);
      } else {
        setShowLoading(true);
        setFallback(false);
      }

      let activeParam = null;
      let body = {};

      if (filter === "active") {
        activeParam = true;
      } else if (filter === "inactive") {
        activeParam = false;
      } else if (filter === "prospect") {
        const prospectIds = prospectPassUsedData
          ? prospectPassUsedData.map((item) => item.id)
          : [];
        body = { ids: prospectIds };
      }

      const response =
        await FINANCIAL_ADVISOR_API.financialAdvisorClientListing(
          page,
          searchQuery,
          limit,
          activeParam,
          body
        );

      if (currentRequestId !== requestIdRef.current) return;

      if (response && response.data) {
        let incomingData = response.data;
        const formattedData = incomingData.map((item) => {
          if (!item.client) return item;
          return {
            ...item,
            client: {
              ...item.client,
              phoneNumber:
                item.client.countryCode && item.client.phoneNumber
                  ? `${item.client.countryCode} ${item.client.phoneNumber}`
                  : item.client.phoneNumber || "",
            },
          };
        });

        let newList;

        if (isFreshSearch || page === 1) {
          newList = formattedData;
        } else {
          newList = [...clientsData, ...formattedData];
        }

        const localData = localStorage.getItem("delegateUsers");
        const parsedData = localData ? JSON.parse(localData) : [];

        const combined = [...newList, ...parsedData];
        // const uniqueData = Array.from(
        //   new Map(combined.map((item) => [item.client.id, item])).values()
        // );
        const uniqueData = Array.from(
          new Map(
            combined.map((item, index) => {
              const clientId = item.client && item.client.id
                ? item.client.id
                : "no-client-" + index;   // fallback key
              return [clientId, item];
            })
          ).values()
        );

        localStorage.setItem("delegateUsers", JSON.stringify(uniqueData));
        dispatch(setDelegateUsers(uniqueData));

        setClientsData(newList);
        setTotalPages(Math.ceil(response.meta.total / 10));
        setTotalItems(response.meta.total);
        setCurrentPage(page);

        // Update stats
        if (response.meta && response.meta.activeClientsCount !== undefined) {
          const activeCount = response.meta.activeClientsCount;
          const totalSlots = 10;
          const availableSeats = Math.max(0, totalSlots - activeCount);
          const totalClientsCount = response.meta.overAllTotal || 0;
          const prospectPassList = Array.isArray(response.meta.prospectPass)
            ? response.meta.prospectPass
            : [];
          const prospectPassAvailable = 3 - prospectPassList.length;
          setStats({
            totalSlots: totalSlots,
            activeClients: activeCount,
            availableSeats: availableSeats,
            totalClients: totalClientsCount,
            prospectPassAvailable: prospectPassAvailable,
          });
          setProspectPassUsedData(prospectPassList);
        }

        if (formattedData && formattedData.length < 1) {
          setFallback(true);
        } else {
          setFallback(false);
        }
      }

      if (currentRequestId === requestIdRef.current) {
        setShowLoading(false);
        setIsFetchingNextPage(false);
      }
    } catch (error) {

      console.log("error while getting Clients list==>", error);
      if (currentRequestId === requestIdRef.current) {
        setShowLoading(false);
        setIsFetchingNextPage(false);
      }
    }
  };

  const handleFilterChange = (newFilter) => {
    const filterToSet = activeFilter === newFilter ? null : newFilter;
    setActiveFilter(filterToSet);
    setClientsData([]);
    setFallback(false);
    getClientListingData(1, query, true, 10, filterToSet);
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight >= scrollHeight) {
      if (currentPage < totalPages && !isFetchingNextPage && !showloading) {
        // console.log("Fetching more items...");
        getClientListingData(currentPage + 1, query, false, 10, activeFilter);
      }
    }
  };

  const loadClientBankAccountStatus = async () => {
    try {
      const response =
        await FINANCIAL_ADVISOR_API.fetchAdvisorClientsBankListing();
      console.log(
        "ProfessionalViewDashboard fetchAdvisorClientsBankListing response:",
        response
      );
      setAdvisorClientBankAccounts(
        response && Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.log(
        "ProfessionalViewDashboard fetchAdvisorClientsBankListing error:",
        error
      );
      setAdvisorClientBankAccounts([]);
    }
  };
  
  useEffect(() => {

    loadClientBankAccountStatus();
  }, []);
  

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, []);

  const refreshAllLoadedData = async () => {
    const currentRequestId = ++requestIdRef.current;
    try {
      setShowLoading(true);
      // Fetch everything from page 1 up to the current items count
      // We use currentPage * 10 as the limit to get all currently loaded clients
      const currentLimit = Math.max(10, currentPage * 10);

      const response = await FINANCIAL_ADVISOR_API.financialAdvisorClientListing(
        1,
        query,
        currentLimit
      );

      if (currentRequestId !== requestIdRef.current) return;

      if (response && response.data) {
        let newList = response.data;
        const formattedData = newList.map((item) => {
          if (!item.client) return item;
          return {
            ...item,
            client: {
              ...item.client,
              phoneNumber:
                item.client.countryCode && item.client.phoneNumber
                  ? `${item.client.countryCode} ${item.client.phoneNumber}`
                  : item.client.phoneNumber || "",
            },
          };
        });

        const localData = localStorage.getItem("delegateUsers");
        const parsedData = localData ? JSON.parse(localData) : [];

        const combined = [...formattedData, ...parsedData];
        const uniqueData = Array.from(
          new Map(
            combined.map((item, index) => {
              const clientId =
                item.client && item.client.id
                  ? item.client.id
                  : "no-client-" + index; // fallback key
              return [clientId, item];
            })
          ).values()
        );

        localStorage.setItem("delegateUsers", JSON.stringify(uniqueData));
        dispatch(setDelegateUsers(uniqueData));

        setClientsData(formattedData);
        setTotalPages(Math.ceil(response.meta.total / 10));
        setTotalItems(response.meta.total);
        // Note: we DON'T update currentPage here, we keep it as is.

        // Update stats
        if (response.meta && response.meta.activeClientsCount !== undefined) {
          const activeCount = response.meta.activeClientsCount;
          const totalSlots = 10;
          const availableSeats = Math.max(0, totalSlots - activeCount);
          const totalClientsCount = response.meta.overAllTotal || 0;
          const prospectPassList = Array.isArray(response.meta.prospectPass)
            ? response.meta.prospectPass
            : [];
          const prospectPassAvailable = 3 - prospectPassList.length;
          setStats({
            totalSlots: totalSlots,
            activeClients: activeCount,
            availableSeats: availableSeats,
            totalClients: totalClientsCount,
            prospectPassAvailable: prospectPassAvailable,
          });
          setProspectPassUsedData(prospectPassList);
        }

        if (formattedData && formattedData.length < 1) {
          setFallback(true);
        } else {
          setFallback(false);
        }
      }

      if (currentRequestId === requestIdRef.current) {
        setShowLoading(false);
      }
    } catch (error) {
      console.log("error while refreshing Clients list==>", error);
      if (currentRequestId === requestIdRef.current) {
        setShowLoading(false);
      }
    }
  };

  const refreshClientData = async () => {
    try {
      setShowLoading(true);
      const response = await FINANCIAL_ADVISOR_API.refreshClientData();

      if (response && response.data) {
        console.log("Client data refreshed successfully");
      }
      setShowLoading(false);
    } catch (error) {
      console.log("error while getting Clients list==>", error);
      setShowLoading(false);
    }
  };

  const openProspectPassModal = () => {
    setProspectPassModalVisible(true);
  };

  const closeProspectPassModal = () => {
    setProspectPassModalVisible(false);
  };

  const formatProspectPassResetDate = (dateValue) => {
    if (!dateValue) {
      return "Not set";
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return "Not set";
    }

    return parsedDate.toLocaleString();
  };

  const getApiMessage = (response) => {
    const responseData = response && response.data ? response.data : null;
    const nestedData =
      responseData && responseData.data ? responseData.data : null;

    return (
      (response && response.message) ||
      (responseData && responseData.message) ||
      (nestedData && nestedData.message) ||
      null
    );
  };

  const refreshPlaidForClient = async (clientId) => {
    if (!clientId) {
      return;
    }

    setRefreshingClientId(clientId);
    try {
      const response = await DEATH_API.refreshBanksData({}, clientId);
      const apiMessage = getApiMessage(response);
      const isSuccess =
        response && (response.status === 200 || response.status === "SUCCESS");

      if (isSuccess) {
        await DEATH_API.refreshLiability(clientId);
        await MODULE_API.refreshAssetModule(clientId);
        swal(
          "Success",
          apiMessage || "Client Plaid data refresh has started.",
          "success"
        );
        await getClientListingData(currentPage, query, true, 10, activeFilter);
      } else if (response && (response.status === 400 || response.status === "ERROR")) {
        swal(
          "Oops!",
          apiMessage || "Unable to refresh client Plaid data.",
          "error"
        );
      } else {
        swal(
          "Oops!",
          apiMessage || "Unable to refresh client Plaid data at the moment.",
          "error"
        );
      }
    } catch (error) {
      console.log("error while refreshing client plaid data==>", error);
      const apiMessage =
        (error && error.response && error.response.data && error.response.data.message) ||
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.data &&
          error.response.data.data.message) ||
        (error && error.message);
      swal(
        "Oops!",
        apiMessage || "Unable to refresh client Plaid data.",
        "error"
      );
    } finally {
      setRefreshingClientId(null);
    }
  };
  const handleResendInvitation = (id, clientData) => {
    swal({
      title: "Are you sure?",
      content: {
        element: "div",
        attributes: {
          innerHTML: `You are about to resend Invitation to <strong>${clientData.name}</strong>. Are you sure you want to continue?`,
          style: "text-align: center;",
        },
      },
      dangerMode: true,
      buttons: ["No, cancel it!", "Yes, resend it!"],
      icon: "warning",
      type: "warning",
      closeOnConfirm: false,
      closeOnCancel: false,
    }).then(async function (isConfirm) {
      // Redirect the user
      if (isConfirm) {
        try {
          setProcessingClientId(id);
          let res = await FINANCIAL_ADVISOR_API.resendInvitation(
            id
          );
          if (res && res.status === 200) {
            swal("Resent!", "Your Invitation has been resent.", "success");
          }
          setProcessingClientId(null);
        } catch (error) {
          setProcessingClientId(null);
          console.log("error while deleting Client", error);
        }
      }
    });
  };

  const deleteClient = (id, clientData) => {
    swal({
      title: "Are you sure?",
      content: {
        element: "div",
        attributes: {
          innerHTML: `You are about to permanently delete <strong>${clientData.name}</strong>. This action cannot be undone. All associated financial data, documents, and history will be erased forever from Asset Planet. This may have legal and compliance implications for your record-keeping requirements.`,
          style: "text-align: center;",
        },
      },
      dangerMode: true,
      buttons: ["No, cancel it!", "Yes, delete it!"],
      icon: "warning",
      type: "warning",
      closeOnConfirm: false,
      closeOnCancel: false,
    }).then(async function (isConfirm) {
      // Redirect the user
      if (isConfirm) {
        try {
          setProcessingClientId(id);
          // setShowLoading(true);
          let res = await FINANCIAL_ADVISOR_API.deletefinancialAdvisorClient(
            id, "delete"
          );
          if (res && res.status === 200) {
            swal("Deleted!", "Your Client has been deleted.", "success");
            const delegateUsers = JSON.parse(
              localStorage.getItem("delegateUsers") || "[]"
            );

            const updatedUsers = delegateUsers.filter(
              (item) => item.client && item.client.id !== id
            );
            dispatch(setDelegateUsers(updatedUsers));

            localStorage.setItem("delegateUsers", JSON.stringify(updatedUsers));

            // Refresh all loaded data from server to ensure sync
            await refreshAllLoadedData();
          }
          // setShowLoading(false);
          setProcessingClientId(null);
        } catch (error) {
          // setShowLoading(false);
          setProcessingClientId(null);
          console.log("error while deleting Client", error);
        }
      }
    });
  };
  const activateFinancialAdvisorClient = (id, clientData) => {
    swal({
      title: "Are you sure?",
      content: {
        element: "div",
        attributes: {
          innerHTML: `Are you sure you want to activate <strong>${clientData.name}</strong>?`,
          style: "text-align: center;",
        },
      },
      dangerMode: true,
      buttons: ["No, cancel it!", "Yes, activate it!"],
      icon: "warning",
      type: "warning",
      closeOnConfirm: false,
      closeOnCancel: false,
    }).then(async function (isConfirm) {
      // Redirect the user
      if (isConfirm) {
        try {
          setProcessingClientId(id);
          // setShowLoading(true);
          const payload = { clientId: id };
          let res = await FINANCIAL_ADVISOR_API.activateFinancialAdvisorClient(
            payload
          );
          if (res && res.status === 200) {
            swal({
              title: "Activated!",
              content: {
                element: "div",
                attributes: {
                  innerHTML: `Client <strong>${clientData.name}</strong> is now active and their data is being refreshed.`,
                  style: "text-align: center;",
                },
              },
              icon: "success",
              type: "success",
            });
            const delegateUsers = JSON.parse(
              localStorage.getItem("delegateUsers") || "[]"
            );

            const updatedUsers = delegateUsers.filter(
              (item) => item.client && item.client.id !== id
            );
            dispatch(setDelegateUsers(updatedUsers));

            localStorage.setItem("delegateUsers", JSON.stringify(updatedUsers));

            // Refresh all loaded data from server to ensure sync
            await refreshAllLoadedData();
          }
          if (res && res.status === 400) {
            swal("Error", res.message, "error");
          }
          // setShowLoading(false);
          setProcessingClientId(null);
        } catch (error) {
          // setShowLoading(false);
          setProcessingClientId(null);
          console.log("error while activating Client", error);
        }
      }
    });
  };
  const deactivateFinancialAdvisorClient = (id, clientData) => {
    setSelectedClientForDeactivation(clientData);
    setNotifyClient(false);
    setEmailContent(
      `<p>Dear ${clientData.name},</p><p>Your account access has been temporarily suspended. Please contact your advisor for more information.</p><p>Best regards,</p><p>Asset Planet Team</p>`
    );
    setDeactivateModalVisible(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedClientForDeactivation) return;

    const clientData = selectedClientForDeactivation;
    const id = clientData.id;

    try {
      setProcessingClientId(id);
      // setShowLoading(true);
      setDeactivateModalVisible(false);

      const payload = {
        clientId: id,
        // notify: notifyClient,
        template: notifyClient ? emailContent : null
      };

      let res = await FINANCIAL_ADVISOR_API.deactivateFinancialAdvisorClient(payload);

      if (res && res.status === 200) {
        swal({
          title: "Deactivated!",
          content: {
            element: "div",
            attributes: {
              innerHTML: `Client <strong>${clientData.name}</strong> has been made inactive. This active slot will now be on a 24-hour cooldown.`,
              style: "text-align: center;",
            },
          },
          icon: "success",
          type: "success",
        });

        const delegateUsers = JSON.parse(
          localStorage.getItem("delegateUsers") || "[]"
        );

        const updatedUsers = delegateUsers.filter(
          (item) => item.client && item.client.id !== id
        );
        dispatch(setDelegateUsers(updatedUsers));

        localStorage.setItem("delegateUsers", JSON.stringify(updatedUsers));

        // Refresh all loaded data from server to ensure sync
        await refreshAllLoadedData();
      }
      if (res && res.status === 400) {
        swal("Error", res.message, "error");
      }
      // setShowLoading(false);
      setProcessingClientId(null);
    } catch (error) {
      // setShowLoading(false);
      setProcessingClientId(null);
      console.log("error while deactivating Client", error);
      swal("Error", "Something went wrong during deactivation", "error");
    }
  };

  const offboardFinancialAdvisorClient = (id, clientData) => {
    swal({
      title: "Off-board Client?",
      content: {
        element: "div",
        attributes: {
          innerHTML: `You are about to off-board <strong>${clientData.name}</strong>. This will disconnect the client from your advisor account but keep their data intact so they can purchase their own consumer subscription.<br/><br/>This is an alternative to permanent deletion. Are you sure you want to continue?`,
          style: "text-align: center;",
        },
      },
      dangerMode: true,
      buttons: ["No, cancel", "Yes, Off-board Client"],
      icon: "warning",
      type: "warning",
      closeOnConfirm: false,
      closeOnCancel: false,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          setProcessingClientId(id);
          // setShowLoading(true);
          const payload = { clientId: id };
          let res = await FINANCIAL_ADVISOR_API.deletefinancialAdvisorClient(
            id, "offboard"
          );
          if (res && res.status === 200) {
            swal(
              "Off-boarded!",
              `Client ${clientData.name} has been off-boarded successfully.`,
              "success"
            );
            const delegateUsers = JSON.parse(
              localStorage.getItem("delegateUsers") || "[]"
            );

            const updatedUsers = delegateUsers.filter(
              (item) => item.client && item.client.id !== id
            );
            dispatch(setDelegateUsers(updatedUsers));

            localStorage.setItem("delegateUsers", JSON.stringify(updatedUsers));

            // Refresh all loaded data from server to ensure sync
            await refreshAllLoadedData();
          } else {
            swal(
              "Error",
              res && res.message ? res.message : "Failed to off-board client",
              "error"
            );
          }
          // setShowLoading(false);
          setProcessingClientId(null);
        } catch (error) {
          // setShowLoading(false);
          setProcessingClientId(null);
          console.log("error while off-boarding Client", error);
          swal("Error", "Something went wrong", "error");
        }
      }
    });
  };

  const prospectPassClient = (id, clientData) => {
    swal({
      title: "Use Prospect Pass?",
      content: {
        element: "div",
        attributes: {
          innerHTML: `You are about to use a <strong>Prospect Pass</strong> on <strong>${clientData.name}</strong>. This will allow them to access the portal for a limited time without using an active seat. <br/><br/><strong>Usage Limit:</strong> Your plan includes 3 Prospect Passes.<br/><strong>Replenishment:</strong> Once a pass is used, it will automatically regenerate after 30 days.`,
          style: "text-align: center;",
        },
      },
      dangerMode: false,
      buttons: ["No, cancel", "Yes, use Prospect Pass"],
      icon: "info",
      type: "info",
      closeOnConfirm: false,
      closeOnCancel: false,
    }).then(async function (isConfirm) {
      if (isConfirm) {
        try {
          setProcessingClientId(id);
          // setShowLoading(true);
          const payload = { clientId: id, prospectPass: true };
          let res = await FINANCIAL_ADVISOR_API.activateFinancialAdvisorClient(
            payload
          );
          if (res && res.status === 200) {
            swal({
              title: "Prospect Pass Activated!",
              content: {
                element: "div",
                attributes: {
                  innerHTML: `<strong>${clientData.name}</strong> now has temporary access to the portal via Prospect Pass. This pass has been used and cannot be applied again for this client.`,
                  style: "text-align: center;",
                },
              },
              icon: "success",
              type: "success",
            });
            const delegateUsers = JSON.parse(
              localStorage.getItem("delegateUsers") || "[]"
            );

            const updatedUsers = delegateUsers.filter(
              (item) => item.client && item.client.id !== id
            );
            dispatch(setDelegateUsers(updatedUsers));

            localStorage.setItem("delegateUsers", JSON.stringify(updatedUsers));

            // Refresh all loaded data from server to ensure sync
            await refreshAllLoadedData();
          }
          if (res && res.status === 400) {
            swal("Error", res.message, "error");
          }
          // setShowLoading(false);
          setProcessingClientId(null);
        } catch (error) {
          // setShowLoading(false);
          setProcessingClientId(null);
          console.log("error while applying prospect pass", error);
          swal(
            "Error",
            "Failed to apply Prospect Pass. Please try again.",
            "error"
          );
        }
      }
    });
  };
  // Helper function to get processing status for a client
  const getClientProcessingStatus = (clientId) => {
    if (!plaidProcessingData || plaidProcessingData.length === 0) {
      return null;
    }
    return plaidProcessingData.find((item) => item.userId === clientId);
  };

  return (
    <div className="dashboardMainContainer" style={{ position: "relative", minHeight: "100vh" }}>
      <div className="professional-view-search-filter">
        <img
          src={searchIcon}
          alt="searchNewClientIcon"
          className="search-icon"
        />
        <input
          type="text"
          name="search"
          placeholder="Search Client"
          value={query}
          onChange={handleChange}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData
              .getData("text")
              .replace(/[^a-zA-Z0-9\s]/g, "")
              .replace(/\s{2,}/g, " ");
            setQuery(pasted);
          }}
        />
        {loading && <div>Loading....</div>}
      </div>
      {/* {suggestions.length > 0 && (
          <ul className="suggestion-list">
            {suggestions.map((item, idx) => (
              <li key={idx}>{item}</li> // customize based on your API response
            ))}
          </ul>
        )} */}

      <div className="mainHeader">
        <h3 className="professionalTitle" style={{
          display: "flex",
          alignItems: "baseline",
          columnGap: "3px",
        }}>Advisor Panel </h3>
        <div className="video-button-main-container">
          <ModalVideo module="Financial Advisor" autoOpen={true} />
          <div className="button-syncs">
            {showloading && (
              <Spin
                indicator={
                  <Icon
                    type="loading"
                    style={{ fontSize: 32, color: "#2a3da3", fontWeight: "bold" }}
                    spin
                  />
                }
                style={{ marginRight: "15px", display: "flex", alignItems: "center" }}
              />
            )}
            <div className="tooltip-container" style={{alignItems:"flex-start"}}>
              <Button
                type="primary"
                id="add-new-client-btn"
                disabled={showloading}
                onClick={() => {
                  refreshClientData();
                }}
              >
                <Icon type="sync" style={{ fontWeight: "bold" }} />
                <span className="addNewClientbtnText">
                  {showloading ? "Refreshing" : "Refresh All Clients"}
                </span>
              </Button>
              <ToolTipCreator name="manual_sync" />
            </div>
            {isShowClientBtn && (
              <div className="tooltip-container" style={{alignItems:"flex-start"}}>
              <Button
                type="primary"
                id="add-new-client-btn"
                onClick={() => {
                  setModalVisible(true);
                }}
              >
                <img
                  src={addIcon}
                  alt="addNewClientIcon"
                  id="addNewClientBtnIcon"
                />
                <span className="addNewClientbtnText">Add New Client</span>
              </Button>
              <ToolTipCreator name="add_new_client" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="statsContainer">
        <div
          className={`statCard totalClients ${activeFilter === null ? "active" : ""}`}
          onClick={() => handleFilterChange(null)}
          style={{ cursor: "pointer" }}
        >
          <div className="statIconWrapper">
            <Icon type="user" className="statIcon" />
          </div>
          <div className="statNumber">{stats.totalClients}</div>
          <div className="tooltip-container">
            <div className="statLabel">Total Clients</div>
            <ToolTipCreator name="total_clients" />
          </div>
        </div>
        <div className="statCard totalSlots">
          <div className="statIconWrapper">
            <Icon type="team" className="statIcon" />
          </div>
          <div className="statNumber">{stats.totalSlots}</div>
          <div className="tooltip-container">
            <div className="statLabel">Total Slots</div>
            <ToolTipCreator name="total_slots" />
          </div>
        </div>
        <div
          className={`statCard activeClients ${activeFilter === "active" ? "active" : ""}`}
          onClick={() => handleFilterChange("active")}
          style={{ cursor: "pointer" }}
        >
          <div className="statIconWrapper">
            <Icon type="check-circle" className="statIcon" />
          </div>
          <div className="statNumber">{stats.activeClients}</div>
          <div className="tooltip-container">
            <div className="statLabel">Active Clients</div>
            <ToolTipCreator name="active_clients" />
          </div>
        </div>
        <div
          className={`statCard inactiveClients ${activeFilter === "inactive" ? "active" : ""}`}
          onClick={() => handleFilterChange("inactive")}
          style={{ cursor: "pointer" }}
        >
          <div className="statIconWrapper">
            <Icon type="pause-circle" className="statIcon" />
          </div>
          <div className="statNumber">
            {stats.totalClients - stats.activeClients}
          </div>
          <div className="tooltip-container">
            <div className="statLabel">Inactive Clients</div>
            <ToolTipCreator name="inactive_clients" />
          </div>
        </div>
        <div className="statCard availableSeats">
          <div className="statIconWrapper">
            <Icon type="lock" className="statIcon" />
          </div>
          <div className="statNumber">{stats.availableSeats}</div>
          <div className="tooltip-container">
            <div className="statLabel">Available Seats</div>
            <ToolTipCreator name="available_seats" />
          </div>
        </div>
        <div
          className={`statCard prospectPassTotal ${activeFilter === "prospect" ? "active" : ""}`}
          onClick={() => handleFilterChange("prospect")}
          style={{ cursor: "pointer" }}
        >
          <div className="statIconWrapper">
            <Icon type="gift" className="statIcon" />
          </div>
          <div className="statNumber">{stats.prospectPassAvailable}</div>
          <div className="tooltip-container statLabelWithIcon">
            <div className="statLabel">Prospect Passes Available</div>
            <button
              type="button"
              className="prospectPassInfoButton"
              onClick={(e) => {
                e.stopPropagation();
                openProspectPassModal();
              }}
              aria-label="View prospect pass details"
            >
              <Icon type="info-circle" />
            </button>
            <ToolTipCreator name="prospect_pass" />
          </div>
        </div>
      </div>
      {/* === Module Intro Video Section === */}
      {/* <ModuleTourVideo module="Financial Advisor" /> */}
      {/* OR */}

      <div id="professionalViewDisplay" ref={scrollRef} onScroll={handleScroll}>
        {showloading && (
          <div className="main-content-loader">
            <Spin
              indicator={
                <Icon
                  type="loading"
                  style={{ fontSize: 48, color: "#2a3da3" }}
                  spin
                />
              }
            />
            <span className="loader-text">Fetching clients...</span>
          </div>
        )}
        {clientsData && clientsData.length > 0 ? (
          clientsData.map((item, index) => {
            const processingStatus = getClientProcessingStatus(item.client.id);
            const bankSummary = getClientBankSummary(item.client.id);
            return (
              <div className="clientView" key={index}>
                <ClientChart data={item} isActionLoading={processingClientId === item.client.id} />
                <ClientDetails
                  data={item}
                  deleteClient={deleteClient}
                  activateFinancialAdvisorClient={activateFinancialAdvisorClient}
                  deactivateFinancialAdvisorClient={
                    deactivateFinancialAdvisorClient
                  }
                  handleRefreshClientData={refreshPlaidForClient}
                  isRefreshLoading={refreshingClientId === item.client.id}
                  prospectPassUsedData={prospectPassUsedData}
                  prospectPassClient={prospectPassClient}
                  processingStatus={processingStatus}
                  offboardFinancialAdvisorClient={offboardFinancialAdvisorClient}
                  isActionLoading={processingClientId === item.client.id}
                  handleResendInvitation={handleResendInvitation}
                  bankSummary={bankSummary}
                  onViewBankAccounts={() =>
                    openBankAccountsModal(item, bankSummary)
                  }
                />
              </div>
            );
          })
        ) : !showloading && showFallback ? (
          <div className="fallback-container">
            <h1 className="fallback-text">No Match Found..</h1>
          </div>
        ) : null}
        {isFetchingNextPage && (
          <div className="bottom-loader-container">
            <Spin
              indicator={
                <Icon
                  type="loading"
                  style={{ fontSize: 24, color: "#2a3da3" }}
                  spin
                />
              }
            />
            <span className="loader-text">Loading more clients...</span>
          </div>
        )}
      </div>
      {totalItems > 10 && (
        <div className="pagination-wrapper">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10}
            onChange={(page) => {
              scrollRef.current.scrollTo(0, 0);
              getClientListingData(page, query, true, 10, activeFilter);
            }}
            showSizeChanger={false}
          />
        </div>
      )}
      <AddClientModal
        open={isModalVisible}
        showLoader={showLoader}
        setShowLoader={setShowLoader}
        reset={reset}
        setReset={setReset}
        onCancel={() => {
          setModalVisible(false);
          setShowLoader(false);
          setReset(true);
        }}
        onSave={async (data) => {
          console.log("Form Submitted:", data);
          setShowLoader(true);
          setShowLoading(true);
          setModalVisible(false);
          try {
            const res = await DASHBOARD_API.addNewAdvisorClient(data);
            if (res && res.status === 200) {
              swal("Success!", "Client Added Successfully", "success");

              setClientsData([]);

              // Fetch updated list
              await getClientListingData(1);
            } else {
              swal(
                "Oops!",
                res && res.message ? res.message : "Failed to add client",
                "error"
              );
            }
            setShowLoading(false);
          } catch (error) {
            console.error("Add client error:", error);
            swal("Oops!", "Something went wrong. Please try again", "error");
          } finally {
            setShowLoader(false);
            setShowLoading(false);
            setReset(true);
          }
        }}
        formData={formData}
        setFormData={setFormData}
      />

      <DeactivateClientModal
        visible={deactivateModalVisible}
        onOk={handleDeactivateConfirm}
        onCancel={() => setDeactivateModalVisible(false)}
        clientData={selectedClientForDeactivation}
        notifyClient={notifyClient}
        setNotifyClient={setNotifyClient}
        emailContent={emailContent}
        setEmailContent={setEmailContent}
      />

      <Modal
        visible={prospectPassModalVisible}
        footer={null}
        onCancel={closeProspectPassModal}
        centered
        width={500}
        className="prospect-pass-modal"
      >
        <div className="prospect-pass-modal__content">
          <div className="prospect-pass-modal__header">
            <div className="prospect-pass-modal__heading">
              <div className="prospect-pass-modal__icon">
                <Icon type="info-circle" />
              </div>
              <div className="prospect-pass-modal__heading-copy">
                <div className="prospect-pass-modal__eyebrow">Prospect Pass</div>
                <h3 className="prospect-pass-modal__title">
                  Pass Status Overview
                </h3>
                <p className="prospect-pass-modal__subtitle">
                  A quick look at your current pass allowance and reset window.
                </p>
              </div>
            </div>
            <div className="prospect-pass-modal__badge">
              {prospectPassStatus
                ? `${prospectPassStatus.remaining || 0} remaining`
                : "Loading"}
            </div>
          </div>

          <div className="prospect-pass-modal__summary">
            <div className="prospect-pass-modal__summary-item">
              <span className="prospect-pass-modal__summary-value">
                {prospectPassStatus && prospectPassStatus.monthlyLimit != null
                  ? prospectPassStatus.monthlyLimit
                  : "0"}
              </span>
              <span className="prospect-pass-modal__summary-label">
                Monthly limit
              </span>
            </div>
            <div className="prospect-pass-modal__summary-item">
              <span className="prospect-pass-modal__summary-value">
                {prospectPassStatus && prospectPassStatus.used != null
                  ? prospectPassStatus.used
                  : "0"}
              </span>
              <span className="prospect-pass-modal__summary-label">
                Used
              </span>
            </div>
            <div className="prospect-pass-modal__summary-item">
              <span className="prospect-pass-modal__summary-value">
                {prospectPassStatus && prospectPassStatus.remaining != null
                  ? prospectPassStatus.remaining
                  : "0"}
              </span>
              <span className="prospect-pass-modal__summary-label">
                Remaining
              </span>
            </div>
            <div className="prospect-pass-modal__summary-item">
              <span className="prospect-pass-modal__summary-value">
                {prospectPassStatus && prospectPassStatus.passDurationHours != null
                  ? `${prospectPassStatus.passDurationHours}h`
                  : "N/A"}
              </span>
              <span className="prospect-pass-modal__summary-label">
                Pass duration
              </span>
            </div>
          </div>

          <div className="prospect-pass-modal__card">
            <div className="prospect-pass-modal__card-row">
              <span className="prospect-pass-modal__card-label">Resets at</span>
              <span className="prospect-pass-modal__card-value">
                {formatProspectPassResetDate(
                  prospectPassStatus && prospectPassStatus.poolResetsAt
                )}
              </span>
            </div>
            <div className="prospect-pass-modal__card-row">
              <span className="prospect-pass-modal__card-label">Active passes</span>
              <span className="prospect-pass-modal__card-value">
                {prospectPassStatus &&
                Array.isArray(prospectPassStatus.activePasses)
                  ? prospectPassStatus.activePasses.length
                  : 0}
              </span>
            </div>
            <div className="prospect-pass-modal__list">
              <div className="prospect-pass-modal__list-title">Active Passes</div>
              <div className="prospect-pass-modal__chips">
                {prospectPassStatus &&
                Array.isArray(prospectPassStatus.activePasses) &&
                prospectPassStatus.activePasses.length > 0 ? (
                  prospectPassStatus.activePasses.map((item, index) => (
                    <span className="prospect-pass-modal__chip" key={index}>
                      {item && (item.name || item.clientName || item.clientId)
                        ? item.name || item.clientName || item.clientId
                        : `Pass ${index + 1}`}
                    </span>
                  ))
                ) : (
                  <span className="prospect-pass-modal__empty">No active passes</span>
                )}
              </div>
            </div>
            <div className="prospect-pass-modal__list">
              <div className="prospect-pass-modal__list-title">
                Passes This Month
              </div>
              <div className="prospect-pass-modal__chips">
                {prospectPassStatus &&
                Array.isArray(prospectPassStatus.passesThisMonth) &&
                prospectPassStatus.passesThisMonth.length > 0 ? (
                  prospectPassStatus.passesThisMonth.map((item, index) => (
                    <span className="prospect-pass-modal__chip" key={index}>
                      {item && (item.name || item.clientName || item.clientId)
                        ? item.name || item.clientName || item.clientId
                        : `Entry ${index + 1}`}
                    </span>
                  ))
                ) : (
                  <span className="prospect-pass-modal__empty">
                    No pass usage this month
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        visible={bankAccountsModalVisible}
        footer={null}
        onCancel={closeBankAccountsModal}
        centered
        width={620}
        className="client-bank-modal"
      >
        <div className="client-bank-modal__content">
          <div className="client-bank-modal__header">
            <div className="client-bank-modal__heading">
              <div className="client-bank-modal__icon">
                <Icon type="credit-card" />
              </div>
              <div className="client-bank-modal__heading-copy">
                <div className="client-bank-modal__eyebrow">Bank Status</div>
                <h3 className="client-bank-modal__title">
                  {selectedBankClient && selectedBankClient.clientName
                    ? selectedBankClient.clientName
                    : "Client"}{" "}
                  Accounts
                </h3>
                <p className="client-bank-modal__subtitle">
                  Linked bank accounts, grouped by bank and status.
                </p>
              </div>
            </div>
            <div className="client-bank-modal__badge">
              {selectedBankClient && selectedBankClient.hasData
                ? `${selectedBankClient.totalBanks || 0} banks`
                : "No banks linked"}
            </div>
          </div>

          <div className="client-bank-modal__summary">
            <div className="client-bank-modal__summary-item">
              <span className="client-bank-modal__summary-value">
                {selectedBankClient && selectedBankClient.totalBanks != null
                  ? selectedBankClient.totalBanks
                  : 0}
              </span>
              <span className="client-bank-modal__summary-label">Banks</span>
            </div>
            <div className="client-bank-modal__summary-item">
              <span className="client-bank-modal__summary-value">
                {selectedBankClient && selectedBankClient.totalAccounts != null
                  ? selectedBankClient.totalAccounts
                  : 0}
              </span>
              <span className="client-bank-modal__summary-label">Accounts</span>
            </div>
            <div className="client-bank-modal__summary-item">
              <span className="client-bank-modal__summary-value">
                {selectedBankClient &&
                selectedBankClient.authorizedAccounts != null
                  ? selectedBankClient.authorizedAccounts
                  : 0}
              </span>
              <span className="client-bank-modal__summary-label">
                Authorized
              </span>
            </div>
            <div className="client-bank-modal__summary-item">
              <span className="client-bank-modal__summary-value">
                {selectedBankClient &&
                selectedBankClient.unauthorizedAccounts != null
                  ? selectedBankClient.unauthorizedAccounts
                  : 0}
              </span>
              <span className="client-bank-modal__summary-label">
                Unauthorized
              </span>
            </div>
          </div>

          <div className="client-bank-modal__body">
            {selectedBankClient &&
            Array.isArray(selectedBankClient.accounts) &&
            selectedBankClient.accounts.length > 0 ? (
              Object.entries(
                selectedBankClient.accounts.reduce((acc, account) => {
                  const bankName =
                    account && account.bankName ? account.bankName : "Unknown bank";
                  if (!acc[bankName]) {
                    acc[bankName] = [];
                  }
                  acc[bankName].push(account);
                  return acc;
                }, {})
              ).map(([bankName, accounts]) => (
                <div className="client-bank-modal__bank-group" key={bankName}>
                  <div className="client-bank-modal__bank-group-header">
                    <div>
                      <div className="client-bank-modal__bank-name">
                        {bankName}
                      </div>
                      <div className="client-bank-modal__bank-meta">
                        {accounts.length} account{accounts.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="client-bank-modal__bank-status-pill">
                      {accounts.some((account) => {
                        const status = String(
                          account && account.accountStatus ? account.accountStatus : ""
                        )
                          .trim()
                          .toLowerCase();
                        return status === "unauthorized";
                      })
                        ? "Needs attention"
                        : "Connected"}
                    </div>
                  </div>

                  <div className="client-bank-modal__account-list">
                    {accounts.map((account, accountIndex) => {
                      const normalizedStatus = String(
                        account && account.accountStatus ? account.accountStatus : ""
                      )
                        .trim()
                        .toLowerCase();
                      const isUnauthorized = normalizedStatus === "unauthorized";
                      return (
                        <div
                          className="client-bank-modal__account-row"
                          key={`${bankName}-${accountIndex}`}
                        >
                          <div className="client-bank-modal__account-main">
                            <div className="client-bank-modal__account-name">
                              {account && account.accountName
                                ? account.accountName
                                : "Account"}
                            </div>
                            <div className="client-bank-modal__account-mask">
                              {account && account.mask
                                ? `•••• ${account.mask}`
                                : "Mask unavailable"}
                            </div>
                          </div>
                          <div
                            className={`client-bank-modal__status ${
                              isUnauthorized
                                ? "client-bank-modal__status--unauthorized"
                                : "client-bank-modal__status--authorized"
                            }`}
                          >
                            {isUnauthorized ? "Unauthorized" : "Authorized"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              <div className="client-bank-modal__empty">
                <div className="client-bank-modal__empty-icon">
                  <Icon type="credit-card" />
                </div>
                <div className="client-bank-modal__empty-title">
                  Bank accounts not added
                </div>
                <div className="client-bank-modal__empty-copy">
                  This client does not have any linked bank accounts yet.
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>

    </div >
  );
};

export default ProfessionalViewDashboard;
